import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import CookieConsentBanner from '@/components/CookieConsentBanner';

// Lazy loading layouts e guards de autenticação (Issue #299)
const AuthRoutesLayout = lazy(() => import('@/components/layout/AuthRoutesLayout'));
const ProtectedRoute = lazy(() => import('@/components/ProtectedRoute'));
const AppLayout = lazy(() => import('@/components/layout/AppLayout'));

// Lazy loading pages
const HomeLayout = lazy(() => import('@/pages/Home'));
const InicioPage = lazy(() => import('@/pages/Inicio'));
const VenderPage = lazy(() => import('@/pages/Vender'));
const ClientesPage = lazy(() => import('@/pages/Clientes'));
const ProdutosPage = lazy(() => import('@/pages/Produtos'));
const DespesasPage = lazy(() => import('@/pages/Despesas'));
const FiadoPage = lazy(() => import('@/pages/Fiado'));
const ResumoPage = lazy(() => import('@/pages/Resumo'));
const HistoricoPage = lazy(() => import('@/pages/Historico'));
const PagamentosPage = lazy(() => import('@/pages/Pagamentos'));
const LandingPage = lazy(() => import('@/pages/Landing'));
const LoginPage = lazy(() => import('@/pages/Login'));
const RegisterPage = lazy(() => import('@/pages/Register'));
const ConfirmarPage = lazy(() => import('@/pages/Confirmar'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPassword'));
const PerfilPage = lazy(() => import('@/pages/Perfil'));
const AssinaturaPage = lazy(() => import('@/pages/Assinatura'));
const RecorrenciasPage = lazy(() => import('@/pages/Recorrencias'));
const DashboardPage = lazy(() => import('@/pages/Dashboard'));
const RelatoriosPage = lazy(() => import('@/pages/Relatorios'));
const ReciboPage = lazy(() => import('@/pages/Recibo'));
const CatalogPage = lazy(() => import('@/pages/Catalog'));
const PrivacidadePage = lazy(() => import('@/pages/Privacidade'));
const ExclusaoContaPage = lazy(() => import('@/pages/ExclusaoConta'));
const DescadastroPage = lazy(() => import('@/pages/Descadastro'));
const TermosPage = lazy(() => import('@/pages/Termos'));
const AjudaPage = lazy(() => import('@/pages/Ajuda'));
const InstalarIphonePage = lazy(() => import('@/pages/InstalarIphone'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// Loading fallback
const PageLoader = () => (
  <main className="flex min-h-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center text-primary">
      <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      <span className="sr-only">Carregando...</span>
    </div>
  </main>
);

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Rotas públicas (não carregam Amplify nem AuthProvider) */}
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/recibo/:token" element={<ReciboPage />} />
        <Route path="/catalogo/:tenantId" element={<CatalogPage />} />
        <Route path="/privacidade" element={<PrivacidadePage />} />
        <Route path="/exclusao-de-conta" element={<ExclusaoContaPage />} />
        <Route path="/descadastro/:token" element={<DescadastroPage />} />
        <Route path="/termos" element={<TermosPage />} />
        <Route path="/ajuda" element={<AjudaPage />} />
        <Route path="/instalar-iphone" element={<InstalarIphonePage />} />

        {/* Rotas de autenticação e rotas protegidas (sob demanda via AuthRoutesLayout) */}
        <Route element={<AuthRoutesLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/confirmar" element={<ConfirmarPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Rotas autenticadas: AppLayout (header + nav) → HomeLayout (estado
              compartilhado + modais) → telas aninhadas (migração abas → rotas) */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route element={<HomeLayout />}>
              <Route path="/" element={<InicioPage />} />
              <Route path="/vender" element={<VenderPage />} />
              <Route path="/clientes" element={<ClientesPage />} />
              <Route path="/produtos" element={<ProdutosPage />} />
              <Route path="/despesas" element={<DespesasPage />} />
              <Route path="/fiado" element={<FiadoPage />} />
              <Route path="/resumo" element={<ResumoPage />} />
              <Route path="/historico" element={<HistoricoPage />} />
              <Route path="/pagamentos" element={<PagamentosPage />} />
              <Route path="/perfil" element={<PerfilPage />} />
              <Route path="/assinatura" element={<AssinaturaPage />} />
              <Route path="/recorrencias" element={<RecorrenciasPage />} />
              <Route path="/relatorios" element={<RelatoriosPage />} />
            </Route>
            <Route path="/dashboard" element={<DashboardPage />} />
          </Route>
        </Route>

        {/* Fallback para 404 (Página Não Encontrada) */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toaster position="top-right" richColors />
      <CookieConsentBanner />
    </Suspense>
  );
}
